import React, { useEffect, useState, useMemo } from 'react';
import { Button, Badge, Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import { MessageIcon } from '@/components/MessageIcon/MessageIcon';
import { ISession, SipJs } from '@/services/sip-js.service';
import { useStore } from '@/stores';
import muteIcon from '@/public/icons/mute.svg';
import unmuteIcon from '@/public/icons/unmute.svg';
import declineIcon from '@/public/icons/decline.svg';
import holdIcon from '@/public/icons/hold.svg';
import unHoldIcon from '@/public/icons/unhold.svg';
import pauseRecordingIcon from '@/public/icons/pauseRecording.svg';
import unPauseRecordingIcon from '@/public/icons/unPauseRecording.svg';
import { getExtensionFromUri } from '@/shared/helpers/get-from-uri.helper';
import './MinimizedCallBar.scss';

interface MinimizedCallBarProps {
  maximizeCallPage: () => void;
}

export const MinimizedCallBar = observer(
  ({ maximizeCallPage }: MinimizedCallBarProps) => {
    const session: ISession | null = SipJs.getSessionByType('active');

    const [counter, setCounter] = useState<string>('');

    const {
      callStore: { clearCallData, getCallControls, setCallControls },
    } = useStore();

    const { muted, paused, onHold } = getCallControls(session.id);

    useEffect(() => {
      const sub$ = session.timer.timeState$.subscribe((time: string) => {
        setCounter(time);
      });

      setCallControls(session.id, {
        onHold: session?.primalSession?.localHold || false,
      });

      return () => {
        sub$.unsubscribe();
      };
    }, [session]);

    const toggleMuteCall = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (muted) {
        SipJs.unMuteCall(session.id);
      } else {
        SipJs.muteCall(session.id);
      }
      setCallControls(session.id, { muted: !muted });
    };

    const toggleHoldCall = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onHold) {
        SipJs.unHoldCall(session.id);
      } else {
        SipJs.holdCall(session.id);
      }
      setCallControls(session.id, { onHold: !onHold });
    };

    const togglePauseRecording = (e: React.MouseEvent) => {
      e.stopPropagation();
      const ownExtensionId = getExtensionFromUri(SipJs.authData.uri);
      SipJs.pauseOrResumeCall(ownExtensionId, session.xSessionId, !paused);
      setCallControls(session.id, { paused: !paused });
    };

    const declineCall = (e: React.MouseEvent) => {
      e.stopPropagation();
      clearCallData();
      SipJs.declineCall(session.id);
      maximizeCallPage();
    };

    const controls = useMemo(
      () => (
        <>
          <Tooltip
            placement="top"
            title={muted ? 'Unmute a call' : 'Mute a call'}
          >
            <Button
              type="text"
              data-testid="toggle-mute"
              onClick={toggleMuteCall}
              icon={<MessageIcon svgUrl={muted ? unmuteIcon : muteIcon} />}
            />
          </Tooltip>
          <Tooltip
            placement="top"
            title={paused ? 'Continue a recording' : 'Pause a recording'}
          >
            <Button
              type="text"
              data-testid="toggle-recording"
              onClick={togglePauseRecording}
              icon={
                <MessageIcon
                  svgUrl={paused ? unPauseRecordingIcon : pauseRecordingIcon}
                />
              }
            />
          </Tooltip>
          <Tooltip
            placement="top"
            title={onHold ? 'Resume a call' : 'Pause a call'}
          >
            <Button
              type="text"
              data-testid="toggle-call"
              onClick={toggleHoldCall}
              icon={<MessageIcon svgUrl={onHold ? unHoldIcon : holdIcon} />}
            />
          </Tooltip>
          <Button
            danger
            data-testid="drop-call"
            className="drop-call"
            type="primary"
            shape="circle"
            onClick={declineCall}
            icon={<MessageIcon svgUrl={declineIcon} />}
          />
        </>
      ),
      [muted, paused, onHold],
    );

    return (
      <div className="minimized-call-bar" onClick={maximizeCallPage}>
        <div className="timer">
          <div>{counter}</div>
          <Badge color="red" />
        </div>
        <div className="controls">{controls}</div>
      </div>
    );
  },
);
